<template>
  <div>
    <breadcrumbs :home="product.name + ': Reassign lot'"/>

      <v-card flat>
        <v-form v-model="valid" ref="form" :disabled="!edit">
          <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

              <v-row no-gutters>
                <v-col cols="6" sm="4" lg="3">
                  <date-picker v-model="transaction.date" @change="refresh"/>
                </v-col>
              </v-row>

              <v-row class="mb-4" v-if="company.settings.lotTrackingForDutyPaid">
                <v-col>
                  <in-bond v-model="transaction.bonded" @change="changeBond"/>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="6" sm="3" lg="2">
                  <v-combobox v-model="transaction.lotFrom" outlined dense label="From lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
                </v-col>
                <v-col cols="6" sm="3" lg="2">
                  <v-combobox v-model="transaction.lotTo" outlined dense label="To lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
                </v-col>
                <v-col cols="12" sm="6" lg="4" v-if="sites.length>1">
                  <v-autocomplete outlined dense label="Site" v-model="transaction.siteId" :items="sites" item-value="id" item-text="name"/>
                </v-col>
              </v-row>

              <cases-bottles :cases.sync="transaction.cases" :bottles.sync="transaction.bottles" :product="product" :availableCount="available" avaiableSuffix="Available"/>

              <v-row>
                <v-col>
                  <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
                </v-col>
              </v-row>
            </v-container>
        </v-form>
        <v-divider/>
        <v-card-actions class="px-6">
          <template v-if="transactionId && company.permissions.includes('inventory.write')">
            <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                      v-model="edit" label="Edit" hide-details class="mt-0"/>
            <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
          </template>
          <v-spacer/>
          <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
          <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InBond from '@/components/InBond'
import CasesBottles from '@/components/CasesBottles'

export default {
  name: 'InventoryProductsTransferLot',
  mixins: [confirmLeave],
  components: {InBond,CasesBottles},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      product: {sites:[]},
      valid:true,
      saving:false,
			transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), cases:0, bottles: 0,lotFrom:null, lotTo:null, bonded:null, siteId: null,comments:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.transactionId, this.transaction.bonded ? {B262:true} : undefined);
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.siteId &&
                                              (this.transaction.bonded && site.inBondBottles || !this.transaction.bonded && site.dutyPaidBottles))
                                .map(site => site.lot);
    },
    available() {
      let site;
      if (!this.company.settings.lotTrackingForProducts ||
          !this.company.settings.lotTrackingForDutyPaid && !this.transaction.bonded)
        site = this.product.sites.reduce((site, productSite) => {
          if (productSite.siteId === this.transaction.siteId) {
            site.dutyPaidBottles += productSite.dutyPaidBottles;
            site.inBondBottles += productSite.inBondBottles;
          }
          return site;
        }, {dutyPaidBottles:0, inBondBottles:0});
      else
        site = this.product.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lotFrom);
      return (this.transaction.bonded ? site?.inBondBottles : site?.dutyPaidBottles) || 0;
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    this.transaction.siteId = this.sites[0].id;
    if (this.transactionId) {
      const results = await this.$apiCall(`query InventoryProductsTransferLot($id:ID!) {
                                                productTransferLot(id:$id) {
                                                  id,productId,date,siteId,cases,lotFrom,lotTo,bottles,bonded,comments
                                                }
                                              }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.productTransferLot;
      this.quantity = this.transaction.bottles;
      await this.refresh();
    }
    else {
      if (!this.company.settings.lotTrackingForDutyPaid)
        this.transaction.bonded = true;
      this.edit = true;
    }

    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    changeBond() {
      if (!this.sites.some(site => site.id === this.transaction.siteId))
        this.transaction.siteId = this.sites[0].id;
      this.$nextTick(() => this.$refs.form.validate());
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsTransferLot($id:ID!,$asof:DateTime!) {
                                                product(id:$id) {
                                                  id,name,caseSize,mixedPack,containerType
                                                  sites(asof:$asof) {siteId,lot,inBondBottles,dutyPaidBottles}
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date});
      if (!results.error) {
        this.product = results.data.product;
        this.$refs.form.validate();
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      this.$refs.form.validate();
      if (!this.valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryProductsTransferLot($input: ProductTransferLotInput!) {
                                            productTransferLotCreate(input:$input) {id}
                                          }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} reassign lot transaction for ${this.product.name}`});
      }
		}
	}
}
</script>